import { AxiosError } from "axios";
import { QueryClient, DefaultOptions } from "react-query";

import {
  AnyState,
  ErrorState,
  IdleState,
  LoadingState,
  SuccessState,
} from "./query.types";
import { getRuntimeEnvName } from "../../helpers";

export const validQueryStatuses = [
  "idle",
  "loading",
  "error",
  "success",
] as const;
export type QueryStatus = typeof validQueryStatuses[number];

export const queryClientDefaultOptions: DefaultOptions = {
  queries: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  },
};

export const queryClient = new QueryClient({
  defaultOptions: queryClientDefaultOptions,
});

/**
 * Sets the fabricQueryClient on the window for debugging.
 * Does NOT run in production.
 *
 * Do NOT call this function outside this file.
 * It is only exported for testability.
 */
export const setFabricQueryClientOnWindow = (): void => {
  if (getRuntimeEnvName() !== "production") {
    // Allow debugging access to the fabricQueryClient in the console
    // EXCEPT in production as this can be used by malicious actors to mess with data.
    window["fabricQueryClient"] = queryClient;
  }
};
setFabricQueryClientOnWindow();

export const ensureIdleState = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  s: AnyState<Data, ErrorT>,
): IdleState => {
  const { status, error, data } = s;
  if (status === "idle") {
    if (error === null && data === undefined) {
      const idleState: IdleState = { status, error, data: undefined };
      return idleState;
    }
    throw new Error("Invalid idle state");
  }
  throw new Error("Expected idle status");
};

export const ensureLoadingState = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  s: AnyState<Data, ErrorT>,
): LoadingState => {
  const { status, error, data } = s;
  if (status === "loading") {
    if (error === null && data === undefined) {
      const loadingState: LoadingState = { status, error, data: undefined };
      return loadingState;
    }
    throw new Error("Invalid loading state");
  }
  throw new Error("Expected loading status");
};

export const ensureSuccessState = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  s: AnyState<Data, ErrorT>,
  allowUndefinedData?: boolean,
): SuccessState<Data> => {
  const { status, error, data } = s;
  if (status === "success") {
    if (error === null && (data !== undefined || allowUndefinedData)) {
      const ensuredData = data as Data;
      const successState: SuccessState<Data> = {
        status,
        error,
        data: ensuredData,
      };
      return successState;
    }
    throw new Error("Invalid success state");
  }
  throw new Error("Expected success status");
};

export const ensureErrorState = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  s: AnyState<Data, ErrorT>,
): ErrorState<Data | undefined, ErrorT> => {
  const { status, error, data } = s;
  if (status === "error") {
    if (error !== null) {
      const errorState: ErrorState<Data | undefined, ErrorT> = {
        status,
        error,
        data,
      };
      return errorState;
    }
    throw new Error("Invalid error state");
  }
  throw new Error("Expected error status");
};

export const ensureErrorStateWithoutData = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  s: AnyState<Data, ErrorT>,
): ErrorState<undefined, ErrorT> => {
  const { status, error, data } = s;
  if (status === "error") {
    if (error !== null && data === undefined) {
      const errorState: ErrorState<undefined, ErrorT> = {
        status,
        error,
        data: undefined,
      };
      return errorState;
    }
    throw new Error("Invalid error state");
  }
  throw new Error("Expected error status");
};

export const ensureErrorStateWithData = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  s: AnyState<Data, ErrorT>,
  allowUndefinedData?: boolean,
): ErrorState<Data, ErrorT> => {
  const { status, error, data } = s;
  if (status === "error") {
    if (error !== null && (data !== undefined || allowUndefinedData)) {
      const ensuredData = data as Data;
      const errorState: ErrorState<Data, ErrorT> = {
        status,
        error,
        data: ensuredData,
      };
      return errorState;
    }
    throw new Error("Invalid error state");
  }
  throw new Error("Expected error status");
};
